import { useState } from "react";
import AppShell from "@/components/layout/AppShell";
import useAutoRefresh from "@/hooks/useAutoRefresh";
import PhotoUpload from "@/components/common/PhotoUpload";
import StatusBadge from "@/components/common/StatusBadge";
import api, { formatApiErrorDetail } from "@/lib/apiClient";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { MagnifyingGlass } from "@phosphor-icons/react";

export function GateOutInner() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(null);
  const [photoOut, setPhotoOut] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = () => api.get("/gate/in-area").then((r) => setRows(r.data)).catch(()=>{});
  useAutoRefresh(load);

  const filtered = rows.filter((r) =>
    !q ||
    r.vehicle_no?.toLowerCase().includes(q.toLowerCase()) ||
    r.company?.toLowerCase().includes(q.toLowerCase())
  );

  const submit = async () => {
    if (!sel) return toast.error("Pilih kendaraan terlebih dahulu");
    setSubmitting(true);
    try {
      await api.post("/gate/out", { gate_id: sel.id, photo_out: photoOut, notes });
      toast.success(`Gerbang keluar: ${sel.vehicle_no}`);
      setSel(null); setPhotoOut(""); setNotes("");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="grid grid-cols-1 xl:grid-cols-5 gap-6">
        {/* List */}
        <div className="xl:col-span-3 rounded-xl bg-white border border-slate-200 shadow-sm">
          <div className="p-5 border-b border-slate-100 flex items-center gap-3">
            <MagnifyingGlass size={16} className="text-slate-400" />
            <Input
              data-testid="gate-out-search"
              placeholder="Cari plat nomor / perusahaan…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="h-9 border-0 shadow-none px-0 focus-visible:ring-0"
            />
            <span className="text-[11px] font-mono text-slate-500">{filtered.length} kendaraan</span>
          </div>
          <div className="max-h-[540px] overflow-y-auto divide-y divide-slate-100">
            {filtered.length === 0 && (
              <div className="py-16 text-center text-sm text-slate-400">Tidak ada kendaraan di area.</div>
            )}
            {filtered.map((r) => (
              <button
                key={r.id}
                onClick={() => setSel(r)}
                data-testid={`gate-out-row-${r.vehicle_no}`}
                className={`w-full text-left p-4 flex items-center justify-between gap-3 transition-colors hover:bg-slate-50 ${
                  sel?.id === r.id ? "bg-sky-50 border-l-4 border-sky-500" : ""
                }`}
              >
                <div className="min-w-0">
                  <div className="font-mono font-bold text-slate-900 text-[14px]">{r.vehicle_no}</div>
                  <div className="text-[12px] text-slate-500 truncate">
                    {r.company} · {r.driver_name}
                  </div>
                  <div className="text-[10px] text-slate-400 mt-0.5 font-mono">
                    Masuk: {new Date(r.gate_in_at).toLocaleString("id-ID")}
                  </div>
                </div>
                <StatusBadge status={r.status} />
              </button>
            ))}
          </div>
        </div>

        {/* Panel action */}
        <div className="xl:col-span-2 rounded-xl bg-white border border-slate-200 p-6 shadow-sm sticky top-24 self-start">
          <h3 className="font-heading text-sm font-bold uppercase tracking-wider text-slate-800 mb-3">
            Konfirmasi Gerbang Keluar
          </h3>
          {sel ? (
            <div className="space-y-4">
              <div className="rounded-lg bg-slate-50 border border-slate-100 p-4">
                <div className="text-[10px] uppercase tracking-widest text-slate-500">Kendaraan</div>
                <div className="font-mono font-bold text-lg text-slate-900">{sel.vehicle_no}</div>
                <div className="text-xs text-slate-600 mt-1">{sel.company} · {sel.driver_name}</div>
              </div>

              <PhotoUpload label="Foto Kendaraan Keluar" value={photoOut} onChange={setPhotoOut} testid="gate-out-photo" />

              <div>
                <label className="text-[11px] font-bold uppercase tracking-[0.1em] text-slate-600">Catatan</label>
                <Textarea
                  data-testid="gate-out-notes"
                  rows={2}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="mt-1.5"
                  placeholder="Kondisi kendaraan saat keluar…"
                />
              </div>

              <Button
                data-testid="gate-out-submit"
                onClick={submit}
                disabled={submitting}
                className="w-full h-11 bg-slate-900 hover:bg-slate-800 text-white font-semibold"
              >
                {submitting ? "Memproses…" : "Konfirmasi Keluar"}
              </Button>
            </div>
          ) : (
            <div className="py-8 text-center text-xs text-slate-400">
              Pilih kendaraan dari daftar untuk memproses gerbang keluar.
            </div>
          )}
        </div>
      </div>
  );
}

export default function GateOutPage() {
  return (
    <AppShell title="Gerbang Keluar" subtitle="Validasi & catat kendaraan yang keluar area">
      <GateOutInner />
    </AppShell>
  );
}
